import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import _ from 'lodash'

// Utils
import { eventTimezone } from '../../utils/dates'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Handles calculating the total price to display in the modal.
 *
 * `purchasedLicenseCount` is the licenses the exhibitor has *bought* — the free
 * allotment left out, cancelled ones left out, refunded ones kept — counted across
 * every page rather than off a loaded list. It is what decides whether the
 * first-license price applies, and the API decides the same thing from the same set
 * through `EventExhibitorLicense.objects.purchased()`. The two have to agree, because
 * the API recomputes the line items and refuses a submitted amount that disagrees.
 *
 * Not the held count. That one includes the free allotment and drives every license
 * figure on the page, but a free license is a giveaway and does not use the
 * first-license price up.
 * @param {object} data current selections for licenses and rental devices
 * @param {object} pricingInfo pricing from the event
 * @param {number} purchasedLicenseCount licenses the exhibitor has already bought
 * @returns total price
 */
export const calculatePriceTotal = (data, pricingInfo, purchasedLicenseCount) => {
  const firstLicenseCost = parseFloat(pricingInfo.firstPrice)
  const additionalLicenseCost = parseFloat(pricingInfo.additionalPrice)
  const leadRetrievalDeviceRentalCost = parseFloat(pricingInfo.rentalPrice)

  // `id` is also the `value` for licenses and rental devices
  const licenseQuantity = data.numberOfLicenses?.id || 0
  const rentalQuantity = data.numberOfLeadRetrievalDevices?.id || 0

  // An empty license side of the cart costs nothing. The first-license price only applies
  // to an exhibitor's first license, so it was charged whenever the count was 0 —
  // including for a rental-only purchase, where `first + (0 - 1) * additional` quoted the
  // difference between the two license prices on top of the rentals (EXEC-13).
  let totalLicenseCost = 0
  if (licenseQuantity > 0) {
    totalLicenseCost =
      purchasedLicenseCount > 0
        ? licenseQuantity * additionalLicenseCost
        : firstLicenseCost + (licenseQuantity - 1) * additionalLicenseCost
  }

  // Short-circuited on the quantity: an event with rentals turned off has a null rental
  // price, and `parseFloat(null) * 0` is NaN, which would take the whole total down
  const totalLeadRetrievalDeviceRentalCost =
    rentalQuantity > 0 ? leadRetrievalDeviceRentalCost * rentalQuantity : 0

  return totalLicenseCost + totalLeadRetrievalDeviceRentalCost || 0
}

/**
 * Handles calculating the total price when unit prices are manually adjusted (EE-side
 * purchases). Every license is charged at the adjusted price — no first/additional split.
 * @param {object} data current selections, including `licenseUnitPrice` and `rentalUnitPrice`
 * @returns total price
 */
export const calculateAdjustedPriceTotal = (data) => {
  const licenseCost = (parseFloat(data.licenseUnitPrice) || 0) * (data.numberOfLicenses?.id || 0)
  const rentalCost =
    (parseFloat(data.rentalUnitPrice) || 0) * (data.numberOfLeadRetrievalDevices?.id || 0)

  return licenseCost + rentalCost
}

/**
 * Gets pricing data based on the date and event pricing categories.
 * @param {object} event the event to get pricing data for
 * @returns
 */
export const getPricingData = (event) => {
  // Use the event's timezone so pricing windows close at end-of-day in the event's local time.
  const eventTz = eventTimezone(event)
  const today = dayjs().tz(eventTz)

  const pricing = {}

  // Parse end dates then convert to the event timezone before comparing (inclusive of end date)
  if (event.earlyBirdEndsAt && !today.isAfter(dayjs.tz(event.earlyBirdEndsAt, eventTz), 'day')) {
    pricing.firstPrice = event.earlyBirdFirstPrice
    pricing.additionalPrice = event.earlyBirdAdditionalPrice
    pricing.rentalPrice = event.earlyBirdRentalPrice
  } else if (
    event.standardEndsAt &&
    !today.isAfter(dayjs.tz(event.standardEndsAt, eventTz), 'day')
  ) {
    pricing.firstPrice = event.standardFirstPrice
    pricing.additionalPrice = event.standardAdditionalPrice
    pricing.rentalPrice = event.standardRentalPrice
  } else {
    pricing.firstPrice = event.onsiteFirstPrice
    pricing.additionalPrice = event.onsiteAdditionalPrice
    pricing.rentalPrice = event.onsiteRentalPrice
  }

  return pricing
}

// Pulled from https://medium.com/@almestaadmicadiab/how-to-parse-google-maps-address-components-geocoder-response-774d1f3375d
const ADDRESS_MAPPING = {
  streetNumber: ['street_number'],
  zip: ['postal_code'],
  streetName: ['street_address', 'route'],
  state: [
    'administrative_area_level_1',
    'administrative_area_level_2',
    'administrative_area_level_3',
    'administrative_area_level_4',
    'administrative_area_level_5',
  ],
  city: [
    'locality',
    'sublocality',
    'sublocality_level_1',
    'sublocality_level_2',
    'sublocality_level_3',
    'sublocality_level_4',
  ],
  country: ['country'],
}

/**
 * Pulls out relevant address fields from the Google Maps API response.
 * @param {object} place
 * @returns address object
 */
export const configureBillingAddress = (place) => {
  const addressComponents = place.address_components
  if (!addressComponents) return null

  const address = {
    streetNumber: '',
    streetName: '',
    city: '',
    state: '',
    zip: '',
    country: '',
  }

  addressComponents.forEach((component) => {
    const { types } = component
    _.forEach(_.keys(ADDRESS_MAPPING), (key) => {
      if (ADDRESS_MAPPING[key].some((type) => types.includes(type))) {
        address[key] = component.long_name
      }
    })
  })

  return address
}

/**
 *
 * @param {object} billingInfo data from the payment information form
 * @param {object} pricingInfo pricing from the event
 * @param {number} purchasedLicenseCount licenses the exhibitor has already bought,
 *  counted across every page
 * @param {boolean} adjustablePricing when true, include the manually-adjusted unit prices
 *  (EE-side purchases) and compute the total from them
 * @returns
 */
export const getChargeData = async (
  billingInfo,
  pricingInfo,
  purchasedLicenseCount,
  adjustablePricing = false,
) => {
  const {
    firstName,
    lastName,
    cardNumber,
    cardExpiry,
    cardCvc,
    address1,
    address2,
    city,
    state,
    zip,
    country,
  } = billingInfo

  const billTo = {
    firstName,
    lastName,
    address: `${address1}${address2 ? `, ${address2}` : ''}`,
    city,
    state,
    zip,
    country,
  }

  // A zero-total purchase collects no card details, so every one of these may be
  // absent — `requested_amount` of 0 tells the API to skip the payment gateway
  const creditCard = {
    cardNumber: cardNumber ? cardNumber.replace(/\s/g, '') : null,
    expirationDate: cardExpiry ? dayjs(cardExpiry, 'MM / YY').format('YYYY-MM') : null,
    cardCode: cardCvc || null,
  }

  const chargeData = {
    expiration_date: creditCard.expirationDate,
    billing_first_name: billTo.firstName,
    billing_last_name: billTo.lastName,
    billing_address: billTo.address,
    billing_city: billTo.city,
    billing_state: billTo.state,
    billing_zip: billTo.zip,
    billing_country: billTo.country,
    requested_amount: adjustablePricing
      ? calculateAdjustedPriceTotal(billingInfo)
      : calculatePriceTotal(billingInfo, pricingInfo, purchasedLicenseCount),
    credit_card_number: creditCard.cardNumber,
    credit_card_code: creditCard.cardCode,
    num_licenses: billingInfo.numberOfLicenses.id, // `id` is `value`
    num_rental_devices: billingInfo.numberOfLeadRetrievalDevices.id, // `id` is `value`
  }

  if (adjustablePricing) {
    chargeData.license_unit_price = parseFloat(billingInfo.licenseUnitPrice) || 0
    chargeData.rental_device_unit_price = parseFloat(billingInfo.rentalUnitPrice) || 0
  } else {
    // Only the exhibitor's own flow collects a T&C agreement; the API stamps the
    // timestamp itself from this flag rather than trusting one sent by the client
    chargeData.terms_accepted = billingInfo.termsAccepted === true
  }

  return chargeData
}
